import React, { useCallback, useEffect, useMemo, useState } from "react";
import QRCode from "react-qr-code";
import { Watch, RefreshCw, Link2, ShieldOff, Radio, Wifi, WifiOff, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const fmt = (v) => v ? new Date(v).toLocaleString() : "—";

export default function AppleWatchPanel() {
  const { admin } = useAuth();
  const [devices, setDevices] = useState([]);
  const [users, setUsers] = useState([]);
  const [pairing, setPairing] = useState(null);
  const [pairCrew, setPairCrew] = useState([]);
  const [targetCrewId, setTargetCrewId] = useState("");
  const [supervisorConfig, setSupervisorConfig] = useState({ modules: [], crew: [] });
  const [supervisorCrewId, setSupervisorCrewId] = useState("");
  const [supervisorModules, setSupervisorModules] = useState([]);
  const [savingSupervisor, setSavingSupervisor] = useState(false);
  const [targetUser, setTargetUser] = useState(admin?.id || "self");
  const [loading, setLoading] = useState(false);
  const [demo, setDemo] = useState({ module_id: "spa", title: "Guest service call", cabin_name: "Owner Suite" });
  const canRevoke = ["developer", "admin"].includes(admin?.role);
  const canPairOther = ["developer", "admin", "manager"].includes(admin?.role);
  const canDemo = ["developer", "admin", "manager", "supervisor"].includes(admin?.role);

  const load = useCallback(async () => {
    try {
      const d = await api.get("/admin/watch/devices");
      try { const c = await api.get("/admin/watch/crew"); setPairCrew(c.data?.items || []); } catch (_) { setPairCrew([]); }
      try {
        const s = await api.get("/admin/watch/supervisor-config");
        setSupervisorConfig({ modules: s.data?.modules || [], crew: s.data?.crew || [] });
      } catch (_) { setSupervisorConfig({ modules: [], crew: [] }); }
      setDevices(d.data?.items || []);
      if (canPairOther) {
        try { const u = await api.get("/admin/users"); setUsers((u.data || []).filter((x) => x.crew_id)); } catch { /* role may not list users */ }
      }
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not load Watch devices"); }
  }, [canPairOther]);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const pair = async () => {
    setLoading(true);
    try {
      const payload = targetUser && targetUser !== "self" ? { user_id: targetUser } : {};
      const r = await api.post("/watch/pair/start", targetCrewId ? { ...payload, target_crew_id: targetCrewId } : payload);
      setPairing(r.data);
      toast.success("Pairing session created");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not start pairing"); }
    finally { setLoading(false); }
  };

  const setCrewAvailability = async (d, state) => {
    if (!d.crew_id) return;
    try {
      await api.patch(`/admin/watch/crew/${d.crew_id}/availability`, { state });
      setDevices((prev) => prev.map((x) => x.id === d.id ? { ...x, availability: state } : x));
      toast.success(`${d.crew_name || "Crew"} set ${state.replaceAll("_", " ").toLowerCase()}`);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not change availability"); }
  };

  const revoke = async (d) => {
    if (!window.confirm(`Revoke ${d.device_name || "Apple Watch"} for ${d.crew_name || "crew"}?`)) return;
    try { await api.post(`/admin/watch/devices/${d.id}/revoke`); toast.success("Watch revoked"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not revoke Watch"); }
  };

  const demoCall = async () => {
    try {
      await api.post("/admin/watch/demo-call", { ...demo, guest_name: "Demo Guest", notes: "Generated from SLX-one Watch console", priority: "normal" });
      toast.success("Demo call sent to eligible Watches");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not create demo call"); }
  };

  const active = useMemo(() => devices.filter((d) => d.active).length, [devices]);
  return <div className="space-y-8">
    <div className="flex items-start justify-between gap-4">
      <div>
        <h2 className="font-display text-3xl flex items-center gap-3"><Watch className="h-7 w-7"/>Apple Watch · Crew Connect</h2>
        <p className="text-sm text-muted-foreground mt-1">Pair, monitor and revoke crew Watches. Watch credentials are independent of the crew password.</p>
      </div>
      <Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4 mr-2"/>Refresh</Button>
    </div>

    <section className="grid lg:grid-cols-[1fr_360px] gap-6">
      <div className="border border-border bg-card p-6 space-y-5">
        <div>
          <div className="font-display text-2xl">Pair a Watch</div>
          <p className="text-sm text-muted-foreground mt-1">The crew member signs into SLX-one once on web, then enters this short-lived code on the Watch. No password is typed on the Watch.</p>
        </div>
        {canPairOther && users.length > 0 && <div>
          <div className="text-sm font-medium mb-2">Crew account</div>
          <Select value={targetUser} onValueChange={setTargetUser}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>
            <SelectItem value="self">My account</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
          </SelectContent></Select>
        </div>}
        <Button onClick={pair} disabled={loading}><Link2 className="h-4 w-4 mr-2"/>{loading ? "Creating…" : "Add / Pair Apple Watch"}</Button>
        <div className="rounded-xl border border-border/60 bg-card/40 p-4 mb-4">
          <div className="text-xs uppercase tracking-[.18em] text-primary/70 font-semibold">Admin / Developer provisioning</div>
          <div className="font-semibold mt-1">Pair Watch for Crew Member</div>
          <p className="text-xs text-muted-foreground mt-1 mb-3">Select the Crew Profile that this Watch will represent. Your Admin/Developer account does not need its own Crew Profile.</p>
          <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={targetCrewId} onChange={(e) => setTargetCrewId(e.target.value)}>
            <option value="">My linked Crew Profile (personal pairing)</option>
            {pairCrew.map((c) => <option key={c.id} value={c.id}>{c.name}{c.rank ? " · " + c.rank : ""}{c.department ? " · " + c.department : ""}</option>)}
          </select>
        </div>

        <div className="rounded-xl border border-amber-400/30 bg-amber-400/[0.04] p-4 mb-4">
          <div className="text-xs uppercase tracking-[.18em] text-amber-500/80 font-semibold">WATCH SUPERVISOR MODE</div>
          <div className="font-semibold mt-1">Live operational incident tracking</div>
          <p className="text-xs text-muted-foreground mt-1 mb-3">Supervisors receive informational Watch alerts for every lifecycle event in selected modules — new bookings/requests, time changes, cancellations, acceptance, bounce/release and completion. Supervisor mode does not claim a request or grant assignment rights.</p>
          <select
            className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm mb-3"
            value={supervisorCrewId}
            onChange={(e) => {
              const id = e.target.value;
              setSupervisorCrewId(id);
              const crew = supervisorConfig.crew.find((c) => c.id === id);
              setSupervisorModules(crew?.supervisor_modules || []);
            }}
          >
            <option value="">Select Crew Profile for Supervisor Mode</option>
            {supervisorConfig.crew.map((c) => <option key={c.id} value={c.id}>{c.name}{c.rank ? " · " + c.rank : ""}{c.department ? " · " + c.department : ""}</option>)}
          </select>
          {supervisorCrewId && <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            {supervisorConfig.modules.map((m) => {
              const checked = supervisorModules.includes(m.id);
              return <label key={m.id} className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 cursor-pointer ${checked ? "border-amber-400/50 bg-amber-400/10" : "border-border/60 bg-background/30"}`}>
                <span className="text-sm">{m.title}</span>
                <span className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                  Supervisor
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => setSupervisorModules((prev) => checked ? prev.filter((x) => x !== m.id) : [...prev, m.id])}
                  />
                </span>
              </label>;
            })}
          </div>}
          <button
            className="h-9 rounded-md bg-amber-500 text-black px-4 text-sm font-semibold disabled:opacity-40"
            disabled={!supervisorCrewId || savingSupervisor}
            onClick={async () => {
              if (!supervisorCrewId) return;
              setSavingSupervisor(true);
              try {
                await api.patch(`/admin/watch/crew/${supervisorCrewId}/supervisor-modules`, { modules: supervisorModules });
                toast.success("Supervisor coverage updated");
                const s = await api.get("/admin/watch/supervisor-config");
                setSupervisorConfig({ modules: s.data?.modules || [], crew: s.data?.crew || [] });
              } catch (e) {
                toast.error(formatErr(e.response?.data?.detail) || "Could not update Supervisor Mode");
              } finally { setSavingSupervisor(false); }
            }}
          >{savingSupervisor ? "Saving…" : "Save Supervisor Coverage"}</button>
        </div>
        {pairing && <div className="rounded-xl border border-accent/40 bg-accent/5 p-5 flex flex-col sm:flex-row items-center gap-6">
          <div className="bg-white p-3 rounded-xl"><QRCode size={128} value={`slxone://pair?code=${pairing.code}`} /></div>
          <div className="text-center sm:text-left">
            <div className="text-xs uppercase tracking-[.22em] text-muted-foreground">Pairing code</div>
            <div className="font-mono text-4xl tracking-[.28em] mt-2">{pairing.code}</div>
            <div className="text-xs text-muted-foreground mt-3">One-time 4-digit pairing code. After confirmation, this Watch stays paired with a long-lived device credential until an administrator explicitly revokes it or the Watch is manually unpaired.</div>
            <div className="text-sm mt-3">{pairing.crew_name} · {pairing.vessel_name}</div>
            <div className="text-xs text-muted-foreground mt-1">Expires {fmt(pairing.expires_at)}</div>
          </div>
        </div>}
      </div>

      <div className="border border-border bg-card p-6">
        <div className="text-xs uppercase tracking-[.2em] text-muted-foreground">Fleet Watch status</div>
        <div className="font-display text-5xl mt-2">{active}</div>
        <div className="text-sm text-muted-foreground">active paired devices</div>
        <div className="mt-5 text-xs text-muted-foreground">LIVE mode uses <strong>https://app.slx-one.com</strong>. DEMO mode is isolated on-device and never writes production data.</div>
      </div>
    </section>

    {canDemo && <section className="border border-border bg-card p-6">
      <div className="font-display text-xl flex items-center gap-2"><FlaskConical className="h-5 w-5"/>Watch dispatch test</div>
      <p className="text-sm text-muted-foreground mt-1 mb-4">Creates a real SLX request marked as demo so you can test pairing, eligibility, atomic Accept, Bounce and Complete end-to-end.</p>
      <div className="grid md:grid-cols-3 gap-3">
        <Select value={demo.module_id} onValueChange={(v) => setDemo((d) => ({...d,module_id:v}))}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>
          {['spa','guest_requests','service','yoga','personal_training','gym'].map((m) => <SelectItem key={m} value={m}>{m.replaceAll('_',' ')}</SelectItem>)}
        </SelectContent></Select>
        <Input value={demo.title} onChange={(e) => setDemo((d) => ({...d,title:e.target.value}))}/>
        <div className="flex gap-2"><Input value={demo.cabin_name} onChange={(e) => setDemo((d) => ({...d,cabin_name:e.target.value}))}/><Button onClick={demoCall}><Radio className="h-4 w-4 mr-2"/>Send</Button></div>
      </div>
    </section>}

    <section>
      <div className="flex items-center gap-3 mb-3"><h3 className="font-display text-2xl">Paired Watches</h3><Badge variant="outline">{devices.length}</Badge></div>
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]"><thead className="bg-muted/40"><tr className="text-left">
          <th className="p-3">Crew</th><th className="p-3">Device</th><th className="p-3">Availability</th><th className="p-3">Connection</th><th className="p-3">App</th><th className="p-3">Last seen</th><th className="p-3">Credential</th><th className="p-3 text-right">Action</th>
        </tr></thead><tbody>
          {devices.map((d) => <tr key={d.id} className="border-t border-border/70">
            <td className="p-3 font-medium">{d.crew_name || "—"}</td>
            <td className="p-3"><div>{d.device_name || "Apple Watch"}</div><div className="text-xs text-muted-foreground">{d.device_model || "watchOS"}</div></td>
            <td className="p-3"><Select value={d.availability || "AVAILABLE"} onValueChange={(v) => setCrewAvailability(d, v)}><SelectTrigger className="h-8 min-w-[130px]"><SelectValue/></SelectTrigger><SelectContent><SelectItem value="AVAILABLE">Available</SelectItem><SelectItem value="BUSY">Busy</SelectItem><SelectItem value="OFF_DUTY">Off duty</SelectItem><SelectItem value="ON_BREAK">On break</SelectItem><SelectItem value="GUEST_SERVICE">Guest service</SelectItem><SelectItem value="TEMPORARILY_UNAVAILABLE">Temporarily unavailable</SelectItem></SelectContent></Select></td>
            <td className="p-3">{d.connection_state === 'live' ? <span className="inline-flex gap-1 items-center text-emerald-600"><Wifi className="h-4 w-4"/>Live</span> : <span className="inline-flex gap-1 items-center text-muted-foreground"><WifiOff className="h-4 w-4"/>{d.connection_state || 'offline'}</span>}</td>
            <td className="p-3">{d.app_version || "—"}</td><td className="p-3 text-xs">{fmt(d.last_seen_at)}</td>
            <td className="p-3"><Badge variant={d.active ? "default" : "destructive"}>{d.credential_status}</Badge></td>
            <td className="p-3 text-right">{canRevoke && d.active && <Button size="sm" variant="outline" className="text-destructive" onClick={() => revoke(d)}><ShieldOff className="h-4 w-4 mr-2"/>Revoke</Button>}</td>
          </tr>)}
          {!devices.length && <tr><td colSpan="8" className="p-8 text-center text-muted-foreground">No Apple Watches paired yet.</td></tr>}
        </tbody></table>
      </div>
    </section>
  </div>;
}
